/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { TENANTS } from "../constants"
import { isStrangerTenant } from "../core/utils"
import {
  CUSTOMER_DATE_FORMAT,
  DATE_FORMAT,
  SERVER_DATE_FORMAT,
  STEP_SUMMARY
} from "../digital-journey/ClientPage/constants/ClientPageConstants"
import moment from "moment"

const handleDateFormat = (date, sourceFormat, targetFormat) => {
  if (!date || !sourceFormat || !targetFormat) {
    return null
  } else {
    return moment(date, sourceFormat).format(targetFormat)
  }
}

const checkDataPreFilled = (value, tunnelData, field) => {
  if (!tunnelData.comeBackToClientStep && tunnelData.step !== STEP_SUMMARY) {
    // Quand le parcours simplifié est absent (field undefined), conserver la valeur client
    return field == null || field?.preFilled ? value : null
  } else {
    return value
  }
}

const handleBirthdate = (birthDate, country) => {
  if (new Date(birthDate).getFullYear() !== new Date().getFullYear()) {
    return handleDateFormat(birthDate, DATE_FORMAT.identity.birthDate[country], SERVER_DATE_FORMAT)
  } else return null
}

const normalizeSpanishMobilePhone = (phoneNumber) => {
  if (!phoneNumber) {
    return phoneNumber
  }
  let digits = String(phoneNumber).replace(/\D/g, "")
  if (digits.startsWith("0034")) {
    digits = digits.substring(2)
  }
  while (digits.startsWith("3434")) {
    digits = digits.substring(2)
  }
  if (/^[67]\d{8}$/.test(digits)) {
    digits = "34" + digits
  }
  return digits ? "+" + digits : phoneNumber
}

const formatPhoneForForm = (phoneNumber, country, tunnelData) => {
  if (!isStrangerTenant(tunnelData.tenant)) {
    return phoneNumber
  }
  if (country === "ES") {
    return normalizeSpanishMobilePhone(phoneNumber)?.replace("+", "")
  }
  return phoneNumber?.replace("+", "")
}

const formatPhoneForTunnelData = (phoneNumber, country, tunnelData) => {
  if (!isStrangerTenant(tunnelData.tenant)) {
    return phoneNumber
  }
  if (country === "ES") {
    return normalizeSpanishMobilePhone(phoneNumber)
  }
  return phoneNumber ? "+" + String(phoneNumber).replace(/^\+/, "") : phoneNumber
}

const getSignatories = (signatories, country, customer) => {
  if (signatories) {
    const signatoriesList = signatories?.map((signatory) => {
      return {
        title: signatory.title,
        name: signatory.name,
        secondSurname: signatory.secondSurname,
        firstName: signatory.firstName,
        signatoryBirthdate: handleDateFormat(
          signatory.signatoryBirthdate,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.identity.birthDate[country]
        ),
        email: signatory.email,
        phoneNumber: signatory.phoneNumber?.replace("+", "")
      }
    })

    return signatoriesList
  } else {
    return [
      {
        title: customer.title,
        name: customer.name,
        firstName: customer.firstName,
        signatoryBirthdate: handleDateFormat(
          customer.birthDate,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.identity.birthDate[country]
        ),
        email: customer.email,
        phoneNumber: customer.shippingAddress.mobilePhone?.replace("+", "")
      }
    ]
  }
}

const isDatePreFilled = (person, tunnelData, customer, country) => {
  const birthDate = person.birthDate ? person.birthDate : customer.companyExecutiveBirthdate
  const birthDateFormat = person.birthDate ? SERVER_DATE_FORMAT : CUSTOMER_DATE_FORMAT
  return new Date(birthDate).getFullYear() === new Date().getFullYear()
    ? null
    : checkDataPreFilled(
        handleDateFormat(birthDate, birthDateFormat, DATE_FORMAT.identity.birthDate[country]),
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDate
      )
}

export function tunnelDataToFinalForm(person, customer, country, tunnelData) {
  let signatoryList
  if (tunnelData?.tenant === TENANTS.BPLS) {
    signatoryList = getSignatories(customer?.signatories, country, customer)
  }
  if (!person || !customer) return null

  return {
    identity: {
      title: checkDataPreFilled(
        person.title,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.title
      ),
      name: checkDataPreFilled(
        person.name,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.name
      ),
      secondSurname: person.secondSurname,
      firstName: checkDataPreFilled(
        person.firstName,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.firstName
      ),
      identityDocumentType: checkDataPreFilled(
        customer.identityDocumentType,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentType
      ),
      citizenShipCountry: checkDataPreFilled(
        person.citizenshipCountryParam4,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.citizenShipCountry
      ),
      birthDate:
        new Date(person.birthDate).getFullYear() === new Date().getFullYear()
          ? null
          : checkDataPreFilled(
              handleDateFormat(person.birthDate, SERVER_DATE_FORMAT, DATE_FORMAT.identity.birthDate[country]),
              tunnelData,
              tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDate
            ),
      birthCountry: checkDataPreFilled(
        person.birthCountryParam4,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCountry
      ),
      birthDepartment: checkDataPreFilled(
        person.birthDepartmentId,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthDepartment
      ),
      birthCity: checkDataPreFilled(
        person.birthCity,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.birthCity
      ),
      taxNumber: checkDataPreFilled(
        customer.taxNumber,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.taxNumber
      )?.toUpperCase(),
      identificationExpirationDate: checkDataPreFilled(
        handleDateFormat(
          customer.identificationExpirationDate,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.identity.identificationExpirationDate[country]
        ),
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.identificationExpirationDate
      ),
      typeOfResidence: checkDataPreFilled(
        customer.typeOfResidence,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.typeOfResidence
      ),
      isSmallBusiness: checkDataPreFilled(
        customer.isSmallBusiness ?? customer.smallBusiness,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.identity?.isSmallBusiness
      )
    },
    coordinates: {
      address: checkDataPreFilled(
        customer.address?.address,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.address
      ),
      additionalAddress: checkDataPreFilled(
        customer.address?.additionalAddress,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.additionalAddress
      ),
      locality: checkDataPreFilled(
        customer.address?.locality,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.locality
      ),
      city: checkDataPreFilled(
        customer.address?.city,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.city
      ),
      postalCode: checkDataPreFilled(
        customer.address?.postalCode,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.postalCode
      ),
      phoneNumber: checkDataPreFilled(
        formatPhoneForForm(customer.address?.mobilePhone || customer.address?.phone, country, tunnelData),
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.phoneNumber
      ),
      fixePhoneNumber: checkDataPreFilled(
        customer.address?.phone,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.fixePhoneNumber
      ),
      email: checkDataPreFilled(
        customer.email || customer.address?.email,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.email
      ),
      iban: checkDataPreFilled(
        customer.bankAccountDTO?.iban,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.iban
      ),
      bic: checkDataPreFilled(
        customer.bankAccountDTO?.bic,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.bic
      ),
      yearBankSeniority: checkDataPreFilled(
        handleDateFormat(
          customer.yearBankSeniority,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.coordinates.yearBankSeniority[country]
        ),
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.yearBankSeniority
      ),
      province: checkDataPreFilled(
        customer.address?.province,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.province
      ),
      countryOfResidence: checkDataPreFilled(
        customer.address?.countryOfResidence,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.coordinates?.countryOfResidence
      )
    },
    situation: {
      profession: checkDataPreFilled(
        customer.occupationCode,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.profession
      ),
      employmentDate: checkDataPreFilled(
        handleDateFormat(customer.employmentDate, SERVER_DATE_FORMAT, DATE_FORMAT.situation?.employmentDate[country]),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.employmentDate
      ),
      workingSituation: checkDataPreFilled(
        customer.employmentContractType,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.employmentContractType
      ),
      endContractDate: checkDataPreFilled(
        handleDateFormat(customer.endContractDate, SERVER_DATE_FORMAT, DATE_FORMAT.situation?.endContractDate[country]),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.endContractDate
      ),
      housingType: checkDataPreFilled(
        customer.address?.housingType,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.housingType
      ),
      residenceDate: checkDataPreFilled(
        handleDateFormat(
          customer.address?.residenceDate,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.situation?.residenceDate[country]
        ),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.residenceDate
      ),
      maritalStatus: checkDataPreFilled(
        customer.maritalStatus,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.maritalStatus
      ),
      dependentChildrenNumber: checkDataPreFilled(
        customer.dependentChildrenNumber,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.dependentChildrenNumber
      ),
      netMonthlyIncome: checkDataPreFilled(
        customer.netMonthlyIncome,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.netMonthlyIncome
      ),
      companyName: checkDataPreFilled(
        customer.companyName,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.companyName
      ),
      companyCity: checkDataPreFilled(
        customer.companyCity,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.companyCity
      ),
      companyProvince: checkDataPreFilled(
        customer.companyProvince,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.companyProvince
      ),
      yearBankSeniority: checkDataPreFilled(
        handleDateFormat(
          customer.yearBankSeniority,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.situation?.yearBankSeniority[country]
        ),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.yearBankSeniority
      ),
      workingSector: checkDataPreFilled(
        customer.workingSector,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.workingSector
      ),
      lineOfBusiness: checkDataPreFilled(
        customer.lineOfBusiness,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.lineOfBusiness
      ),
      professionalUse: checkDataPreFilled(
        customer.professionalUse,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.professionalUse
      ),
      countryOfActivity: checkDataPreFilled(
        customer.countryOfActivity,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.countryOfActivity
      ),
      vatBusinessCode: checkDataPreFilled(
        customer.vatBusinessCode,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.situation?.vatBusinessCode
      ),
      maidenName: checkDataPreFilled(
        person.maidenName,
        tunnelData,
        tunnelData?.simplifiedJourneyInputConfiguration?.situation?.maidenName
      )
    },
    budget: {
      netMonthlyIncome: checkDataPreFilled(
        customer.netMonthlyIncome,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.netMonthlyIncome
      ),
      numberMonthsIncome: checkDataPreFilled(
        customer.numberMonthsIncome,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.numberMonthsIncome
      ),
      otherIncome: checkDataPreFilled(
        customer.otherIncome,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.otherIncome
      ),
      rent: checkDataPreFilled(customer.rent, tunnelData, tunnelData.simplifiedJourneyInputConfiguration?.budget?.rent),
      otherCharges: checkDataPreFilled(
        customer.otherCharges,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.otherCharges
      ),
      incomeOrigin: checkDataPreFilled(
        customer.incomeOrigin,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.incomeOrigin
      ),
      detailOfIncome: checkDataPreFilled(
        customer.detailOfIncome,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.budget?.detailOfIncome
      )
    },
    identityDocuments: {
      citizenshipSimplified: checkDataPreFilled(
        customer.citizenship ? customer.citizenship : customer.citizenshipSimplified,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.citizenshipSimplified
      ),
      citizenshipExtended: checkDataPreFilled(
        customer.citizenshipExtended,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.citizenshipExtended
      ),
      temporaryDocument: checkDataPreFilled(
        customer.temporaryDocument,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.temporaryDocument
      ),
      identityDocumentType: checkDataPreFilled(
        customer.identityDocumentType,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentType
      ),
      identityDocumentsNumber: checkDataPreFilled(
        customer.identityDocumentNumber,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentsNumber
      ),
      documentPlaceOfIssue: checkDataPreFilled(
        customer.identityDocumentCreationLocation,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.documentPlaceOfIssue
      ),
      documentDateOfIssue: checkDataPreFilled(
        handleDateFormat(
          customer.identityDocumentCreationDate ? customer.identityDocumentCreationDate : customer.documentDateOfIssue,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.identityDocuments.documentDateOfIssue[country]
        ),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.documentDateOfIssue
      ),
      identityDocumentsDriverLicenseNumber: checkDataPreFilled(
        customer.identityDocumentsDriverLicenseNumber,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentsDriverLicenseNumber
      ),
      residencePermitType: checkDataPreFilled(
        customer.residencePermitType,
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.residencePermitType
      ),
      residencePermitExpiryDate: checkDataPreFilled(
        handleDateFormat(
          customer.residencePermitExpiryDate,
          SERVER_DATE_FORMAT,
          DATE_FORMAT.identityDocuments.residencePermitExpiryDate[country]
        ),
        tunnelData,
        tunnelData.simplifiedJourneyInputConfiguration?.identityDocuments?.residencePermitExpiryDate
      )
    },
    company: {
      name: customer.corporateName,
      siret: customer.siret?.toString(),
      executiveTitle: person.title,
      executiveLastname: person.name,
      executiveFirstname: person.firstName,
      executiveBirthdate: isDatePreFilled(person, tunnelData, customer, country)
    },
    signatories: signatoryList,
    bankCoordinates: {
      ownerName: customer.bankAccountDTO?.accountOwner,
      iban: customer.bankAccountDTO?.iban,
      accountKey: customer.bankAccountDTO?.accountKey,
      batchId: customer.bankAccountDTO?.batchId
    },
    myAddresses: {
      shippingAddress: customer.shippingAddress?.address,
      shippingAddressComplement: customer.shippingAddress?.additionalAddress,
      zipShippingAddress: customer.shippingAddress?.postalCode,
      cityShippingAddress: customer.shippingAddress?.city,
      billingAddress: customer.address?.address,
      billingAddressComplement: customer.address?.additionalAddress,
      zipBillingAddress: customer.address?.postalCode,
      cityBillingAddress: customer.address?.city
    }
  }
}

export function finalFormValuesToTunnelData(values, country, tunnelData) {
  const IS_BPLS = tunnelData.tenant === TENANTS.BPLS

  const {
    identity,
    coordinates,
    situation,
    budget,
    identityDocuments,
    company,
    signatories,
    bankCoordinates,
    myAddresses
  } = values

  const formattedSignatories = signatories?.map((signatory) => {
    return {
      title: signatory.title,
      firstName: signatory.firstName?.trim(),
      name: signatory.name?.trim(),
      secondSurname: signatory.secondSurname?.trim(),
      signatoryBirthdate:
        new Date(signatory.signatoryBirthdate).getFullYear() === new Date().getFullYear()
          ? null
          : handleDateFormat(signatory.signatoryBirthdate, DATE_FORMAT.identity.birthDate[country], SERVER_DATE_FORMAT),
      email: signatory.email?.trim(),
      phoneNumber: "+" + signatory.phoneNumber
    }
  })
  const personId =
    tunnelData.data.order?.customer?.person?.id ?? tunnelData.data.person?.id ?? tunnelData.data.customer?.personId

  return {
    person: {
      id: personId,
      title: IS_BPLS ? company.executiveTitle : identity.title,
      name: IS_BPLS ? company?.executiveLastname : identity.name,
      secondSurname: identity.secondSurname,
      maidenName: situation?.maidenName,
      firstName: IS_BPLS ? company?.executiveFirstname : identity.firstName,
      citizenshipCountryParam4: identity.citizenShipCountry,
      birthDate: handleBirthdate(IS_BPLS ? company?.executiveBirthdate : identity.birthDate, country),
      birthCountryParam4: identity.birthCountry,
      birthDepartmentId: identity.birthDepartment,
      birthCity: identity.birthCity,
      email: coordinates?.email?.trim()
    },
    customer: {
      personId,
      companySiren: company?.siren,
      companyExecutiveBirthdate: company?.executiveBirthdate,
      companyAddress: company?.address,
      companyAdditionalAddress: company?.additionalAddress,
      companyPostalCode: company?.postalCode,
      companyIsSameAddress: company?.isSameAddress,
      taxNumber: identity.taxNumber?.toUpperCase(),
      identificationExpirationDate: handleDateFormat(
        identity?.identificationExpirationDate,
        DATE_FORMAT.identity?.identificationExpirationDate[country],
        SERVER_DATE_FORMAT
      ),
      email: coordinates.email?.trim(),
      citizenshipSimplified: identityDocuments?.citizenshipSimplified,
      citizenshipExtended: identityDocuments?.citizenshipExtended,
      identityDocumentType: identityDocuments?.identityDocumentType
        ? identityDocuments?.identityDocumentType
        : identity?.identityDocumentType,
      identityDocumentNumber: identityDocuments?.identityDocumentsNumber,
      documentPlaceOfIssue: identityDocuments?.documentPlaceOfIssue,
      identityDocumentsDriverLicenseNumber: identityDocuments?.identityDocumentsDriverLicenseNumber,
      documentDateOfIssue: handleDateFormat(
        identityDocuments?.documentDateOfIssue,
        DATE_FORMAT.identityDocuments.documentDateOfIssue[country],
        SERVER_DATE_FORMAT
      ),
      temporaryDocument: identityDocuments?.temporaryDocument,
      employmentContractType: situation?.workingSituation,
      workingSituation: situation?.workingSituation,
      endContractDate: handleDateFormat(
        situation?.endContractDate,
        DATE_FORMAT.situation.endContractDate[country],
        SERVER_DATE_FORMAT
      ),
      companyName: company?.name ? company?.name : situation?.companyName,
      companyCity: company?.city ? company?.city : situation?.companyCity,
      companyProvince: situation?.companyProvince,
      residencePermitType: identityDocuments?.residencePermitType,
      residencePermitExpiryDate: handleDateFormat(
        identityDocuments?.residencePermitExpiryDate,
        DATE_FORMAT.identityDocuments.residencePermitExpiryDate[country],
        SERVER_DATE_FORMAT
      ),
      address: {
        address: coordinates.address ? coordinates.address : myAddresses?.billingAddress,
        additionalAddress: coordinates.additionalAddress
          ? coordinates.additionalAddress
          : myAddresses?.billingAddressComplement,
        locality: coordinates.locality,
        city: coordinates?.city ? coordinates.city : myAddresses?.cityBillingAddress,
        postalCode: coordinates?.postalCode ? coordinates.postalCode : myAddresses?.zipBillingAddress,
        mobilePhone: formatPhoneForTunnelData(coordinates.phoneNumber, country, tunnelData),
        phone: coordinates.fixePhoneNumber,
        email: coordinates.email,
        housingType: situation?.housingType,
        province: coordinates.province,
        countryOfResidence:
          coordinates.countryOfResidence ||
          (tunnelData.tenant === TENANTS.SAMSUNG_ES_APZ ? { value: "ES" } : undefined),
        residenceDate: handleDateFormat(
          situation?.residenceDate,
          DATE_FORMAT.situation?.residenceDate[country],
          SERVER_DATE_FORMAT
        )
      },
      shippingAddress: {
        address: coordinates?.address ? coordinates.address : myAddresses?.shippingAddress,
        additionalAddress: coordinates?.additionalAddress,
        city: coordinates?.city ? coordinates.city : myAddresses?.cityShippingAddress,
        postalCode: coordinates?.postalCode ? coordinates.postalCode : myAddresses?.zipBillingAddress
      },
      bankAccountDTO: {
        iban: bankCoordinates?.iban ? bankCoordinates.iban : coordinates.iban,
        bic: coordinates.bic,
        accountOwner: bankCoordinates?.ownerName,
        accountKey: coordinates?.accountKey,
        batchId: coordinates?.batchId
      },
      yearBankSeniority:
        handleDateFormat(
          coordinates.yearBankSeniority,
          DATE_FORMAT.coordinates.yearBankSeniority[country],
          SERVER_DATE_FORMAT
        ) ||
        handleDateFormat(
          situation?.yearBankSeniority,
          DATE_FORMAT.situation?.yearBankSeniority[country],
          SERVER_DATE_FORMAT
        ),
      occupationCode: situation?.profession,
      workingSector: situation?.workingSector,
      typeOfResidence: identity?.typeOfResidence,
      lineOfBusiness: situation?.lineOfBusiness,
      professionalUse: situation?.professionalUse,
      countryOfActivity: situation?.countryOfActivity,
      employmentDate: handleDateFormat(
        situation?.employmentDate,
        DATE_FORMAT.situation?.employmentDate[country],
        SERVER_DATE_FORMAT
      ),
      maritalStatus: situation?.maritalStatus,
      dependentChildrenNumber: situation?.dependentChildrenNumber,
      netMonthlyIncome: budget?.netMonthlyIncome || situation?.netMonthlyIncome,
      numberMonthsIncome: budget?.numberMonthsIncome,
      incomeOrigin: budget?.incomeOrigin,
      detailOfIncome: budget?.detailOfIncome,
      otherIncome: budget?.otherIncome,
      rent: budget?.rent,
      otherCharges: budget?.otherCharges,
      corporateName: company?.name,
      siret: company?.siret,
      signatories: formattedSignatories,
      isSmallBusiness: identity?.isSmallBusiness,
      vatBusinessCode: situation?.vatBusinessCode
    }
  }
}
